/*!
 * vue-dropzone-uploader v0.10.17 
 * (c) 2020 Alex Tkachenko
 * Released under the MIT License.
 */
import { Dropzone } from '@aspectus/vue-dropzone';

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

/*
  eslint-disable
    prefer-object-spread,
    import/prefer-default-export,
    max-classes-per-file,
    no-return-assign
*/
var ProgressDescriptor = function ProgressDescriptor(loaded, total) {
  _classCallCheck(this, ProgressDescriptor);

  this.loaded = loaded || 0;
  this.total = total || 0.01;
  this.relative = Math.min(this.loaded / this.total || 0, 1);
};
var FileObject =
/*#__PURE__*/
function () {
  function FileObject(file) {
    _classCallCheck(this, FileObject);

    this.file = file;
    this.name = this.file.name;
    this.size = this.file.size;
    this.type = this.file.type;
    this.url = null;
    this.updateUrl(this.file);
  }

  _createClass(FileObject, [{
    key: "updateUrl",
    value: function updateUrl() {
      var _this = this;

      var file = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this.file;
      var reader = new FileReader();

      reader.onload = function (e) {
        return _this.url = e.target.result;
      };

      reader.readAsDataURL(file);
    }
  }]);

  return FileObject;
}();
var FileDescriptor = function FileDescriptor(file, id, status) {
  _classCallCheck(this, FileDescriptor);

  this.id = id;
  this.file = file;
  this.result = null;
  this.status = status;
  this.progress = new ProgressDescriptor(0, 1);
};

/* eslint-disable prefer-object-spread, no-return-assign, no-param-reassign */
var READY = 'ready';
var PROGRESS = 'progress';
var FAILURE = 'failure';
var Uploader = {
  name: 'vue-dropzone-uploader',
  FileObject: FileObject,
  FileDescriptor: FileDescriptor,
  ProgressDescriptor: ProgressDescriptor,
  props: {
    autoload: {
      type: Boolean,
      default: false
    },
    resource: {
      required: true
    }
  },
  data: function data() {
    this.progressMap = {};
    this.uidPrefix = "dropzone-uploader-file-identifier-".concat(Date.now(), "-");
    this.id = 1;
    return {
      accepted: [],
      rejected: []
    };
  },
  methods: {
    open: function open() {
      this.$refs.dropzone.open();
    },
    getUid: function getUid() {
      return this.uidPrefix + ++this.id;
    },
    makeFile: function makeFile(file) {
      var status = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : READY;
      return new this.$options.FileDescriptor(new this.$options.FileObject(file), this.getUid(), status);
    },
    makeProgress: function makeProgress(a, b) {
      return new this.$options.ProgressDescriptor(a, b);
    },
    finish: function finish(file) {
      var _this = this;

      return function (result) {
        file.result = result;

        _this.$emit('upload', {
          result: result,
          descriptor: file
        });
      };
    },
    reject: function reject(descriptor) {
      this.$emit('reject', {
        descriptor: descriptor
      });
    },
    catch: function _catch(file) {
      var _this2 = this;

      return function (result) {
        file.status = FAILURE;
        file.error = result;
        file.progress = _this2.makeProgress(1, 1);
      };
    },
    progress: function progress(file, _ref) {
      var loaded = _ref.loaded,
          total = _ref.total;

      if (!this.progressMap[file.id]) {
        return;
      }

      file.progress = this.makeProgress(loaded, total);
    },
    upload: function upload(file) {
      var _this3 = this;

      var data = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      var proposal = this.resource.execute(Object.assign({}, data), Object.assign({
        file: file.file.file
      }, data)).then(this.finish(file)).catch(this.catch(file));
      file.status = PROGRESS;
      this.progressMap[file.id] = proposal;

      if (proposal.progress) {
        proposal.progress(function (event) {
          return _this3.progress(file, event);
        });
      }
    },
    remove: function remove(file) {
      var id = file.id;
      var proposal = this.progressMap[id];

      if (proposal && proposal.cancel) {
        proposal.cancel();
      }

      var index = this.accepted.findIndex(function (x) {
        return x.id === id;
      });

      if (index !== -1) {
        this.accepted.splice(index, 1);
      }

      delete this.progressMap[id];
      return true;
    },
    drop: function drop(_ref2) {
      var _this4 = this;

      var accepted = _ref2.accepted,
          rejected = _ref2.rejected;
      var files = accepted.map(function (file) {
        return _this4.makeFile(file, READY);
      });
      var failed = rejected.map(function (file) {
        return _this4.makeFile(file, FAILURE);
      });
      this.rejected = this.rejected.concat(failed);
      this.accepted = this.accepted.concat(files);
      failed.forEach(this.reject);

      if (this.autoload) {
        files.forEach(this.upload);
      }
    }
  },
  render: function render(h) {
    var _this5 = this;

    return h(Dropzone, {
      ref: 'dropzone',
      scopedSlots: Object.assign({}, this.$scopedSlots, {
        default: function _default(props) {
          return _this5.$scopedSlots.default(Object.assign({
            accepted: _this5.accepted,
            rejected: _this5.rejected,
            upload: _this5.upload,
            remove: _this5.remove
          }, props));
        }
      }),
      attrs: this.$attrs,
      on: Object.assign({
        drop: this.drop
      }, this.$listeners)
    });
  }
};

function install(Vue) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref$name = _ref.name,
      name = _ref$name === void 0 ? Uploader.name : _ref$name;

  Vue.component(name, Uploader);
}

var main = {
  install: install
};

export default main;
export { FileDescriptor, FileObject, ProgressDescriptor, Uploader };
